import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { initializeAPIs } from '@/lib/api';

interface CSRFContextType {
  csrfToken: string | null;
  loading: boolean;
  error: string | null;
  ready: boolean;
  refreshToken: () => Promise<void>;
  getToken: () => Promise<string>;
}

const CSRFContext = createContext<CSRFContextType | undefined>(undefined);

const API_BASE_URL = import.meta.env.VITE_API_URL || (import.meta.env.DEV ? 'http://localhost:8000' : '');

export function CSRFProvider({ children }: { children: ReactNode }) {
  const [csrfToken, setCsrfToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);
  const isInitializing = useRef(false);

  const fetchToken = useCallback(async (): Promise<string> => {
    try {
      // Get CSRF token from hidden input field
      const csrfInput = document.querySelector('input[name="csrfmiddlewaretoken"]') as HTMLInputElement;
      
      if (csrfInput && csrfInput.value) {
        const token = csrfInput.value;
        setCsrfToken(token);
        setError(null);
        return token;
      } else {
        throw new Error('No CSRF token found in hidden input field');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to get CSRF token';
      console.error('CSRF token error:', errorMessage);
      setError(errorMessage);
      return '';
    }
  }, []);

  const getToken = useCallback(async (): Promise<string> => {
    if (csrfToken) {
      return csrfToken;
    }

    if (isInitializing.current) {
      // Wait for initialization to complete
      return new Promise((resolve) => {
        const checkToken = () => {
          if (csrfToken) {
            resolve(csrfToken);
          } else {
            setTimeout(checkToken, 100);
          }
        };
        checkToken();
      });
    }

    setLoading(true);
    const token = await fetchToken();
    setLoading(false);
    return token;
  }, [csrfToken, fetchToken]);

  const refreshToken = useCallback(async (): Promise<void> => {
    setCsrfToken(null);
    setLoading(true);
    await fetchToken();
    setLoading(false);
  }, [fetchToken]);

  // Initialize CSRF token on mount
  useEffect(() => {
    if (initialized || isInitializing.current) return;
    
    isInitializing.current = true;
    setLoading(true);
    
    fetchToken().finally(() => {
      setLoading(false);
      setInitialized(true);
      isInitializing.current = false;
    });
  }, [fetchToken, initialized]);

  // Initialize APIs once we have a token getter
  useEffect(() => {
    if (initialized) {
      initializeAPIs(getToken);
    }
  }, [initialized, getToken]);

  const value = {
    csrfToken,
    loading,
    error,
    ready: initialized,
    refreshToken,
    getToken,
  };

  return <CSRFContext.Provider value={value}>{children}</CSRFContext.Provider>;
}

export function useCSRF() {
  const context = useContext(CSRFContext);
  if (context === undefined) {
    throw new Error('useCSRF must be used within a CSRFProvider');
  }
  return context;
}
